/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Stack, Box, Typography, Button, IconButton } from '@mui/material';
import { CloseIcon } from '../../../Components/Icons/Icons';
import { Divider } from 'antd';
import { Checkbox } from 'antd';
import SearchBar from '../../../Components/SearchBar';
import { getColorSettings } from '../../../Utils/helper/commonUtils';
import { AppColors } from '../../../Utils/helper/AppColors';

export default function CampaignSelectDialog({ open, onClose, campaigns, selectedCampaigns, setSelectedCampaigns }) {
	const [localSelected, setLocalSelected] = useState(selectedCampaigns || []);
	const [search, setSearch] = useState('');
	const filteredCampaigns = campaigns.filter((campaign) =>
		campaign.basicDetails?.name.toLowerCase().includes(search.toLowerCase()),
	);

	useEffect(() => {
		if (open) setLocalSelected(selectedCampaigns || []);
	}, [open, selectedCampaigns]);

	const handleToggle = (id) => {
		setLocalSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
	};

	const handleSelectAll = () => {
		setLocalSelected(campaigns.map((c) => c._id));
	};

	const handleReset = () => {
		setLocalSelected([]);
	};

	const handleApply = () => {
		setSelectedCampaigns(localSelected); // Call API or update parent state
		onClose();
	};

	const handleClose = () => {
		onClose(); // Just close, don't update selection
	};

	const localSettingColors = getColorSettings();

	const checkboxStyle = css`
		.ant-checkbox-inner {
			border-radius: 3px !important;
			border: 1px solid ${localSettingColors.buttonBackground} !important;
			background-color: #fff !important;
			transition: border-color 0.2s, background-color 0.2s;
		}
		.ant-checkbox-checked .ant-checkbox-inner {
			background-color: ${localSettingColors.buttonBackground} !important;
			border-color: ${localSettingColors.buttonBackground} !important;
		}
		.ant-checkbox-checked .ant-checkbox-inner::after {
			border-color: #fff !important; /* Makes the checkmark white for contrast */
		}
	`;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth={false} // disables default max-width constraints
			PaperProps={{
				sx: {
					width: '85vw',
					height: '60vh',
					borderRadius: '5px',
				},
			}}
			fullWidth
		>
			<DialogTitle sx={{ borderBottom: '2px solid #DEE2E4', mb: 2 }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Typography sx={{ fontFamily: 'Poppins', fontSize: '18px', fontWeight: 600 }}>Add Campagin</Typography>

					<Stack direction="row" alignItems="center" gap={1}>
						<SearchBar
							variant="small"
							borderRadius="5px"
							color={ AppColors.midnight_black || localSettingColors.fontColor}
						    bgColor={AppColors.lightest_grey || localSettingColors.quinary}
							search={search}
							setSearch={setSearch}
							width="400px"
						/>
						<Stack direction="row" gap={1}>
							<Button
								size="small"
								variant="text"
								sx={{
									border: localSelected.length ? `2px solid ${AppColors.orange}` : 'none',
								}}
								onClick={handleReset}
								disabled={localSelected.length === 0}
							>
								Reset
							</Button>
							<Button
								size="small"
								variant="outlined"
								color='info'
								onClick={handleSelectAll}
								disabled={localSelected.length === campaigns.length}
							>
								Select All
							</Button>
						</Stack>
					</Stack>
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack
					sx={{ maxHeight: 'calc(100vh - 52vh)', overflowY: 'auto', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
					gap={1}
				>
					{filteredCampaigns.map((c) => (
						<Box
							key={c._id}
							sx={(theme) => ({
								backgroundColor: AppColors?.orange || theme.palette.primary.lightest_shade,
								alignItems: 'center',
								padding: '6px 10px',
								borderRadius: '5px',
								cursor:'pointer'
							})}
							onClick={() => handleToggle(c._id)}
						>
							<Stack direction="row" alignItems="center" gap={1}>
								<Checkbox
									checked={localSelected.includes(c._id)}
									// onChange={() => handleToggle(c._id)}
									className={checkboxStyle}
									style={{
										accentColor: localSettingColors.buttonBackground, // This sets the checkmark color
									}}
								/>
								<Typography sx={theme => ({ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14, lineHeight: '150%', color: AppColors?.white || theme.palette.primary.darker_primary })}>
									{c.basicDetails?.name}
								</Typography>
							</Stack>
						</Box>
					))}
				</Stack>
			</DialogContent>
			<Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
				<Button variant="contained" color='info' onClick={handleApply}>
					Apply
				</Button>
			</Stack>
		</Dialog>
	);
}
